<template>
  <b-modal
    modal-class="component-modal-payment"
    dialog-class="payment-modal-dialog"
    id="PaymentModal"
    ref="PaymentModal"
    size="xl"
    title="ชำระเงิน"
    no-close-on-backdrop
    hide-footer
    @hidden="defaultValue"
  >
    <!-- Patient identity strip -->
    <div
      v-if="patient && patient.id"
      class="payment-patient-strip"
    >
      <b-avatar
        :variant="patient.contentId ? 'white' : 'info'"
        :text="patientAbbreviation"
        :src="patient.contentId ? viewFile(patient.contentId) : null"
        size="56px"
        square
        rounded
        class="payment-patient-avatar"
      ></b-avatar>
      <div class="payment-patient-info">
        <div class="payment-patient-name">
          <span class="font-weight-bold">{{ patientFullNameTh }}</span>
          <small class="text-muted ml-2">
            <span v-if="patientFullNameEn">({{ patientFullNameEn }})</span>
            <span v-if="patientGenderLabel"> · {{ patientGenderLabel }} </span>
            <span v-if="patientAgeLabel"> · {{ patientAgeLabel }}</span>
          </small>
        </div>
        <div class="payment-patient-meta text-muted">
          <span v-if="patient.dn"
            >DN <b>{{ patient.dn }}</b></span
          >
          <span v-if="patientPhoneLabel">
            · <i class="fas fa-phone fa-xs"></i> {{ patientPhoneLabel }}
          </span>
          <span v-if="patient.hn">· HN {{ patient.hn }}</span>
        </div>
        <div class="payment-patient-pills">
          <b-badge
            v-if="patient.allergic"
            variant="danger"
            pill
            class="payment-patient-pill"
          >
            <i class="fas fa-exclamation-triangle"></i>
            แพ้ {{ patient.allergic }}
          </b-badge>
          <b-badge
            v-if="patient.congenitalDisease"
            variant="warning"
            pill
            class="payment-patient-pill"
          >
            <i class="fas fa-notes-medical"></i>
            {{ patient.congenitalDisease }}
          </b-badge>
        </div>
      </div>
      <div class="payment-patient-actions">
        <b-button
          size="sm"
          variant="outline-secondary"
          @click="openProfileCustomerModal"
        >
          <i class="fas fa-user"></i> ประวัติคนไข้
        </b-button>
      </div>
    </div>

    <!-- Body: 2-column grid -->
    <div class="payment-body">
      <!-- LEFT COLUMN -->
      <div class="payment-left">
        <!-- Section 1: Next visit -->
        <section class="payment-section">
          <header class="payment-section-header">
            <b-badge
              variant="dark"
              pill
              class="payment-section-num"
              >1</b-badge
            >
            <h6 class="payment-section-title">นัดครั้งต่อไป</h6>
            <b-badge
              v-if="!nextVisitData"
              variant="warning"
              class="payment-section-nudge"
            >
              <i class="fas fa-exclamation-circle"></i>
              แนะนำให้ระบุก่อนชำระ
            </b-badge>
          </header>

          <!-- State: confirmed next visit -->
          <b-card
            v-if="nextVisitData && !nextVisitSkipped"
            class="payment-nextvisit-card payment-nextvisit-card--confirmed"
            body-class="p-3"
          >
            <div class="payment-nextvisit-grid">
              <div class="payment-nextvisit-date">
                <div class="payment-nextvisit-date-day">
                  {{ nextVisitDayLabel }}
                </div>
                <div class="payment-nextvisit-date-month">
                  {{ nextVisitMonthLabel }}
                </div>
              </div>
              <div class="payment-nextvisit-info">
                <div class="payment-nextvisit-info-primary">
                  {{ nextVisitTimeLabel }}
                  <span v-if="nextVisitProcedureLabel">
                    · {{ nextVisitProcedureLabel }}
                  </span>
                  <span v-if="nextVisitDurationLabel">
                    · {{ nextVisitDurationLabel }}
                  </span>
                </div>
                <div class="payment-nextvisit-info-secondary text-muted">
                  <span v-if="nextVisitData.extendedProps.doctorName">
                    {{ nextVisitData.extendedProps.doctorName }}
                  </span>
                </div>
              </div>
              <div class="payment-nextvisit-actions">
                <b-button
                  size="sm"
                  variant="outline-primary"
                  @click="openEventModalEdited(nextVisitData)"
                >
                  <i class="fas fa-pen"></i> เปลี่ยน
                </b-button>
                <b-button
                  size="sm"
                  variant="link"
                  class="text-muted"
                  @click="skipNextVisit"
                >
                  ข้าม
                </b-button>
              </div>
            </div>
          </b-card>

          <!-- State: suggested only (draft, no confirmed) -->
          <b-card
            v-else-if="appointmentDraft && !nextVisitSkipped"
            class="payment-nextvisit-card payment-nextvisit-card--suggested"
            body-class="p-3"
          >
            <div class="payment-nextvisit-suggested-header text-muted">
              <i class="fas fa-lightbulb"></i>
              แพทย์แนะนำให้นัดครั้งต่อไป
            </div>
            <div class="payment-nextvisit-grid mt-2">
              <div class="payment-nextvisit-date payment-nextvisit-date--soft">
                <div class="payment-nextvisit-date-day">
                  +{{ aptDraftDisplay.inDays || 0 }}
                </div>
                <div class="payment-nextvisit-date-month">วัน</div>
              </div>
              <div class="payment-nextvisit-info">
                <div class="payment-nextvisit-info-primary">
                  {{ aptDraftDisplay.duration || 0 }} นาที
                  <span v-if="aptDraftDisplay.treatmentItems">
                    · {{ removeTags(aptDraftDisplay.treatmentItems) }}
                  </span>
                </div>
                <div class="payment-nextvisit-info-secondary text-muted">
                  <span v-if="aptDraftDisplay.doctor">
                    {{ aptDraftDisplay.doctor }}
                  </span>
                  <span v-if="aptDraftDisplay.note">
                    · {{ aptDraftDisplay.note }}
                  </span>
                </div>
              </div>
              <div class="payment-nextvisit-actions">
                <b-button
                  size="sm"
                  variant="primary"
                  @click="openEventModal()"
                >
                  <i class="fas fa-calendar-plus"></i> นัดเลย
                </b-button>
                <b-button
                  size="sm"
                  variant="link"
                  class="text-muted"
                  @click="skipNextVisit"
                >
                  ข้าม
                </b-button>
              </div>
            </div>
          </b-card>

          <!-- State: empty / skipped -->
          <button
            v-else
            type="button"
            class="payment-nextvisit-empty"
            @click="addNextVisit"
          >
            <i class="fas fa-plus-circle"></i>
            <span>เพิ่มนัดครั้งต่อไป</span>
          </button>
        </section>

        <!-- Section 2: Treatment items -->
        <section class="payment-section">
          <header class="payment-section-header">
            <b-badge
              variant="dark"
              pill
              class="payment-section-num"
              >2</b-badge
            >
            <h6 class="payment-section-title">
              รายการรักษา
              <span class="text-muted">({{ visibleItemCount }})</span>
            </h6>
          </header>

          <div v-if="receiptDiscountId" class="payment-discount-notice">
            <i class="fas fa-info-circle"></i>
            ส่วนลดระดับใบเสร็จจะคำนวณเพิ่มบนส่วนลดรายการ
          </div>

          <div
            v-if="isLoading"
            class="text-center text-info my-3"
          >
            <b-spinner small />
            <span class="ml-2">กำลังโหลดรายการ...</span>
          </div>

          <div
            v-else-if="sortedTreatmentItemList.length === 0"
            class="payment-items-empty"
          >
            <div class="text-muted mb-2">
              <i class="fas fa-inbox"></i> ไม่มีรายการรักษา
            </div>
            <b-button
              size="sm"
              variant="outline-secondary"
              @click="openPaymentHistoryModal(appointmentId)"
            >
              <i class="fas fa-caret-left"></i>
              <i class="fas fa-caret-left"></i>
              กลับไปแก้รายการ
            </b-button>
          </div>

          <div
            v-for="(item, idx) in sortedTreatmentItemList"
            :key="(item.treatmentItemId || 'row') + '-' + idx"
            class="payment-item-card"
            :class="getItemCardClass(item)"
          >
            <div class="payment-item-top">
              <div class="payment-item-info">
                <div class="payment-item-title">
                  <span class="payment-item-num">{{ idx + 1 }}.</span>
                  <span>{{ item.name }}</span>
                  <b-badge
                    v-if="item.overdueFlag"
                    variant="danger"
                    pill
                    class="ml-1"
                    >ค้างชำระ</b-badge
                  >
                  <b-badge
                    v-else-if="item.emptyRow"
                    variant="secondary"
                    pill
                    class="ml-1"
                    >ว่าง</b-badge
                  >
                </div>
                <div class="payment-item-meta text-muted">
                  <span>{{ formatDate(item.creationDt) }}</span>
                  <span v-if="item.doctor"> · {{ item.doctor }}</span>
                  <span v-if="item.quantity"> · จำนวน {{ item.quantity }}</span>
                </div>
              </div>
              <div class="payment-item-paid">
                <div class="payment-item-paid-label text-muted">ยอดวันนี้</div>
                <b-form-input
                  v-if="!item.emptyRow"
                  class="text-right payment-item-paid-input"
                  size="sm"
                  v-model="item.paid"
                  number
                  :disabled="isNhso"
                  @change="calPaidItem(item)"
                />
              </div>
            </div>

            <div
              v-if="!item.emptyRow"
              class="payment-item-bottom"
            >
              <div class="payment-item-finals text-muted">
                <span>
                  ยอดสุทธิ
                  <span
                    v-if="item.finalDiscount > 0 && !item.overdueFlag"
                    class="payment-item-strike"
                    >{{ item.price | comma }}</span
                  >
                  <b class="text-dark">{{ item.amount | comma }}</b>
                </span>
                <span class="payment-item-finals-sep">·</span>
                <span>
                  DF <b class="text-dark">{{ (item.finalDf || 0) | comma }}</b>
                </span>
                <span
                  v-if="item.overdueFlag || (item.overdue && item.overdue != 0)"
                  class="payment-item-finals-sep"
                  >·</span
                >
                <span
                  v-if="item.overdueFlag || (item.overdue && item.overdue != 0)"
                  class="text-danger"
                >
                  ค้าง
                  {{
                    (item.overdueFlag ? item.remained : item.overdue) | comma
                  }}
                </span>
              </div>
              <div class="payment-item-actions">
                <DiscountPicker
                  v-if="!item.overdueFlag && item.typeId !== 2"
                  :suggestions="
                    itemDiscountSuggestions[item.treatmentItemId] || []
                  "
                  :value="item.discountId"
                  :disabled="isNhso"
                  @selected="onDiscountSelected(item, $event)"
                />
                <b-button
                  v-if="item.overdueFlag"
                  v-show="isManager || isHeadCounter"
                  size="sm"
                  variant="outline-danger"
                  :disabled="!isManager"
                  @click="openVerifyDeleteOverdueModal(item)"
                >
                  <i class="fas fa-trash-alt"></i>
                </b-button>
                <b-button
                  v-if="!item.overdueFlag"
                  size="sm"
                  variant="outline-secondary"
                  @click="openPaymentHistoryModal(item.appointmentId)"
                  title="แก้รายการรักษา"
                >
                  <i class="fas fa-edit"></i>
                </b-button>
              </div>
            </div>

            <b-collapse :visible="isItemDiscountOpen(item)">
              <div class="payment-item-discount-editor">
                <b-form-group
                  label-cols="3"
                  label="ราคาเต็ม:"
                  label-align="right"
                  label-size="sm"
                >
                  <b-row>
                    <b-input-group
                      size="sm"
                      class="col"
                      append="บาท"
                    >
                      <b-form-input
                        type="number"
                        v-model="item.price"
                        readonly
                      ></b-form-input>
                    </b-input-group>
                    <b-form-radio-group
                      class="align-self-center"
                      size="sm"
                      v-model="item.discountMethod"
                      @change="calDiscount(item)"
                    >
                      <b-form-radio :value="0">ลดปกติ</b-form-radio>
                      <b-form-radio :value="1">ลดแพทย์/คลินิก</b-form-radio>
                    </b-form-radio-group>
                  </b-row>
                </b-form-group>

                <div v-if="item.discountMethod === 0">
                  <b-form-group
                    label-cols="3"
                    label="ส่วนลดปกติ:"
                    label-align="right"
                    label-size="sm"
                    description="*ลดส่วนแพทย์ และคลินิกในอัตราส่วนเท่ากัน"
                  >
                    <b-row>
                      <b-input-group
                        size="sm"
                        class="col"
                      >
                        <b-form-input
                          v-model="item.discountPrice"
                          type="number"
                          @change="calDiscount(item)"
                          @keypress.enter.prevent
                        ></b-form-input>
                        <template #append>
                          <b-input-group-text>
                            {{ item.discountType == 0 ? "%" : "บาท" }}
                          </b-input-group-text>
                        </template>
                      </b-input-group>
                      <b-form-radio-group
                        class="align-self-center"
                        size="sm"
                        v-model="item.discountType"
                        @change="calDiscount(item)"
                      >
                        <b-form-radio :value="0">%</b-form-radio>
                        <b-form-radio
                          :value="1"
                          :disabled="item.dfType === 1"
                          >บาท</b-form-radio
                        >
                      </b-form-radio-group>
                    </b-row>
                  </b-form-group>
                </div>

                <div v-else>
                  <b-form-group
                    label-cols="3"
                    label="วิธีลด:"
                    label-align="right"
                    label-size="sm"
                  >
                    <b-form-radio-group
                      class="pt-1"
                      size="sm"
                      v-model="item.discountType"
                      @change="calDiscount(item)"
                    >
                      <b-form-radio :value="0">%</b-form-radio>
                      <b-form-radio :value="1">บาท</b-form-radio>
                    </b-form-radio-group>
                  </b-form-group>
                  <b-form-group
                    label-cols="3"
                    label="ส่วนลดแพทย์:"
                    label-align="right"
                    label-size="sm"
                  >
                    <b-row>
                      <b-input-group
                        size="sm"
                        class="col"
                        prepend="DF"
                        append="บาท"
                      >
                        <b-form-input
                          :value="
                            item.dfType === 0
                              ? (item.price * item.df) / 100
                              : item.df * (item.quantity || 1)
                          "
                          type="number"
                          disabled
                        ></b-form-input>
                      </b-input-group>
                      <b-input-group
                        size="sm"
                        class="col"
                      >
                        <b-form-input
                          v-model="item.doctorDiscountPrice"
                          type="number"
                          @change="calDiscount(item)"
                          @keypress.enter.prevent
                        ></b-form-input>
                        <template #append>
                          <b-input-group-text>
                            {{ item.discountType == 0 ? "%" : "บาท" }}
                          </b-input-group-text>
                        </template>
                      </b-input-group>
                      <b-input-group
                        v-if="item.discountType === 0"
                        size="sm"
                        class="col"
                        append="บาท"
                      >
                        <b-form-input
                          :value="item.price * (item.doctorDiscountPrice / 100)"
                          readonly
                        ></b-form-input>
                      </b-input-group>
                    </b-row>
                  </b-form-group>
                  <b-form-group
                    label-cols="3"
                    label="ส่วนลดคลินิก:"
                    label-align="right"
                    label-size="sm"
                  >
                    <b-row>
                      <b-input-group
                        size="sm"
                        class="col"
                        prepend="CF"
                        append="บาท"
                      >
                        <b-form-input
                          :value="
                            item.dfType === 0
                              ? item.price - (item.price * item.df) / 100
                              : item.price - item.df * (item.quantity || 1)
                          "
                          type="number"
                          disabled
                        ></b-form-input>
                      </b-input-group>
                      <b-input-group
                        size="sm"
                        class="col"
                      >
                        <b-form-input
                          v-model="item.clinicDiscountPrice"
                          type="number"
                          @change="calDiscount(item)"
                          @keypress.enter.prevent
                        ></b-form-input>
                        <template #append>
                          <b-input-group-text>
                            {{ item.discountType == 0 ? "%" : "บาท" }}
                          </b-input-group-text>
                        </template>
                      </b-input-group>
                      <b-input-group
                        v-if="item.discountType === 0"
                        size="sm"
                        class="col"
                        append="บาท"
                      >
                        <b-form-input
                          :value="item.price * (item.clinicDiscountPrice / 100)"
                          readonly
                        ></b-form-input>
                      </b-input-group>
                    </b-row>
                  </b-form-group>
                  <div class="description text-muted text-right">
                    ส่วนลดเป็นเปอร์เซ็นต์ จะคิดจากราคารวม
                  </div>
                </div>
                <hr />
                <b-form-group
                  label-cols="3"
                  label="ราคาหลังหักส่วนลด:"
                  label-align="right"
                  label-size="sm"
                  class="mb-0"
                >
                  <b-input-group
                    size="sm"
                    append="บาท"
                  >
                    <b-form-input
                      type="number"
                      v-model="item.amount"
                      disabled
                    ></b-form-input>
                  </b-input-group>
                </b-form-group>
              </div>
            </b-collapse>
          </div>

          <!-- Deposit toggle row -->
          <div
            v-if="!isNhso && hasDepositMethods"
            class="payment-deposit-row"
          >
            <small
              v-if="depositBalance > 0"
              class="text-success payment-deposit-balance d-block mb-2"
            >
              <i class="fas fa-piggy-bank"></i>
              ยอดเงินมัดจำคงเหลือ:
              {{ formatCurrency(depositBalance) }} บาท
            </small>
            <div class="payment-deposit-toggle">
              <b-form-checkbox
                v-model="addDeposit"
                switch
              >
                <span class="font-weight-bold">
                  <i class="fas fa-piggy-bank"></i>
                  รับฝากเงินมัดจำ<span v-if="nextVisitDateLabel">
                    สำหรับ {{ nextVisitDateLabel }}</span
                  >
                </span>
              </b-form-checkbox>
            </div>
            <b-collapse :visible="addDeposit">
              <div class="payment-deposit-amount">
                <b-input-group
                  append="บาท"
                  size="sm"
                  class="payment-deposit-input"
                >
                  <b-form-input
                    type="number"
                    v-model="depositAmount"
                    min="0"
                    @change="calDepositAmount"
                    class="text-right"
                  />
                </b-input-group>
                <small class="text-muted d-block mt-1">
                  <i class="fas fa-info-circle"></i>
                  ยอดเงินมัดจำจะถูกบันทึกแยกจากค่ารักษา
                  และสามารถนำมาใช้ชำระในครั้งถัดไป
                </small>
              </div>
            </b-collapse>
          </div>
        </section>
      </div>

      <!-- RIGHT COLUMN: sticky summary rail -->
      <aside class="payment-right">
        <div class="payment-summary-total">
          <div class="payment-summary-label text-muted">
            ยอดที่ต้องชำระวันนี้
          </div>
          <div class="payment-summary-amount text-primary">
            <span class="payment-summary-amount-value">
              {{ formatCurrency(receipt.paid) }}
            </span>
            <span class="payment-summary-amount-unit text-muted">บาท</span>
          </div>
          <div class="payment-summary-sub text-muted">
            <span v-if="visibleItemCount > 0"
              >จาก {{ visibleItemCount }} รายการ</span
            >
            <span v-if="receipt.amount > 0">
              · ยอดสุทธิรวม {{ receipt.amount | comma }}
            </span>
          </div>
          <div
            v-if="receipt.finalDiscount > 0"
            class="payment-summary-discount text-success mt-2"
          >
            <small>
              <i class="fas fa-tag"></i>
              ส่วนลดทั้งสิ้น
              <b>{{ receipt.finalDiscount | comma }}</b> บาท
              <span
                v-if="receiptDiscountLabel"
                class="d-block text-muted"
              >
                {{ receiptDiscountLabel }}
              </span>
            </small>
          </div>
          <div
            v-if="overdueState"
            class="payment-summary-overdue text-danger mt-2"
          >
            <small>
              <i class="fas fa-exclamation-circle"></i>
              ค้างชำระ <b>{{ receipt.overdue | comma }}</b> บาท
            </small>
          </div>
        </div>

        <div
          v-if="receiptDiscountOptions.length > 0"
          class="payment-summary-card"
        >
          <label class="payment-summary-card-label text-muted">
            ส่วนลดระดับใบเสร็จ
          </label>
          <b-form-select
            v-model="receiptDiscountId"
            size="sm"
            :disabled="voucherApplied"
            @change="onReceiptDiscountSelected"
          >
            <b-form-select-option :value="null">
              ไม่ใช้ส่วนลดระดับใบเสร็จ
            </b-form-select-option>
            <b-form-select-option
              v-for="d in receiptDiscountOptions"
              :key="d.discountId"
              :value="d.discountId"
            >
              {{ receiptDiscountOptionLabel(d) }}
            </b-form-select-option>
          </b-form-select>
          <small v-if="voucherApplied" class="text-muted d-block mt-1">
            ใช้บัตรกำนัลแล้ว — ใช้ร่วมกับส่วนลดระดับใบเสร็จไม่ได้
          </small>
          <!-- The picked discount cannot reach this receipt. Said here, at the
               moment it is picked, rather than leaving the operator to find out
               from the save toast after the patient has already paid. -->
          <small
            v-if="receiptDiscountNotice"
            class="text-warning d-block mt-1"
          >
            <i class="fas fa-exclamation-triangle"></i>
            {{ receiptDiscountNotice }}
          </small>
        </div>

        <div class="payment-summary-card">
          <VoucherCodeInput
            :busy="voucherBusy"
            :applied-code="voucherCode"
            @submit="onVoucherSubmit"
            @clear="clearVoucher"
            @open-camera="voucherCameraOpen = true"
          />
          <b-alert
            :show="!!voucherError"
            variant="warning"
            class="mt-2 mb-0 py-1 px-2"
          >
            <small>{{ voucherError }}</small>
          </b-alert>
          <div v-if="voucherInfo && voucherCode" class="text-success small mt-1">
            {{ voucherInfo.batch?.name }}
            <span v-if="voucherInfo.validTo">
              · ใช้ได้ถึง {{ formatVoucherValidTo(voucherInfo.validTo) }}
            </span>
          </div>
        </div>

        <VoucherCameraScanner
          :visible="voucherCameraOpen"
          @decoded="onVoucherDecoded"
          @close="voucherCameraOpen = false"
        />
        <VoucherLinePicker
          :visible="voucherLinesOpen"
          :lines="voucherEligibleLines"
          @pick="onVoucherLinePicked"
          @close="closeVoucherLinePicker"
        />

        <b-alert
          v-if="paymentError"
          show
          variant="danger"
          class="mb-2"
        >
          {{ paymentError }}
        </b-alert>

        <div class="payment-right-spacer"></div>

        <div class="payment-actions">
          <b-button
            variant="primary"
            block
            size="lg"
            :disabled="isLoading"
            @click="openPaymentMethodModal"
            class="payment-cta"
          >
            <b-spinner
              v-if="isLoading"
              small
            />
            <span v-else>
              ชำระเงิน {{ formatCurrency(receipt.paid) }} บาท
            </span>
          </b-button>
          <b-button
            block
            variant="link"
            size="sm"
            @click="openPaymentHistoryModal(appointmentId)"
            class="payment-back text-muted"
          >
            <i class="fas fa-caret-left"></i>
            <i class="fas fa-caret-left"></i>
            แก้ไขรายการรักษา
          </b-button>
        </div>
      </aside>
    </div>

    <PaymentMethodModal
      ref="paymentMethodModal"
      :appointmentLinkedWithKtbPaotang="appointmentLinkedWithKtbPaotang"
      @openPaymentReceiptModal="openPaymentReceiptModal"
    ></PaymentMethodModal>
    <VerifyDeleteOverdueModal
      ref="verifyDeleteOverdueModal"
      :afterDelete="afterDelete"
    />
    <Loading v-if="isLoading"></Loading>
    <Dialog ref="Dialog"></Dialog>
  </b-modal>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { required } from "vuelidate/lib/validators";
import PaymentMethodModal from "@/components/modal/PaymentMethod";
import VerifyDeleteOverdueModal from "@/components/modal/VerifyDeleteOverdue";
import moment from "moment";
import Loading from "@/components/Loading";
import Dialog from "@/components/modal/Dialog";
import { eventBus } from "@/main";
import DiscountPicker from "@/components/discount/DiscountPicker.vue";
import VoucherCodeInput from "@/components/voucher/VoucherCodeInput.vue";
import VoucherCameraScanner from "@/components/voucher/VoucherCameraScanner.vue";
import VoucherLinePicker from "@/components/voucher/VoucherLinePicker.vue";
import { formatPhoneForDisplay } from "@/utils/phoneUtils";
import {
  receiptDiscountOptionLabel,
  receiptDiscountNotice,
} from "@/utils/receiptDiscountOption";

export default {
  name: "Payment",
  components: {
    PaymentMethodModal,
    VerifyDeleteOverdueModal,
    Loading,
    Dialog,
    DiscountPicker,
    VoucherCodeInput,
    VoucherCameraScanner,
    VoucherLinePicker,
  },
  data() {
    return {
      isLoading: false,
      discountSuggestions: {},
      receiptDiscountId: null,
      // Voucher redemption. voucherCode is the only thing sent to the server —
      // the discount is re-resolved from the code server-side, and the validate
      // response deliberately never publishes the discount id.
      voucherCode: null,
      voucherLineId: null,
      voucherInfo: null, // the ok:true validate response, for display only
      voucherError: "",
      voucherBusy: false,
      voucherCameraOpen: false,
      voucherLinesOpen: false,
      voucherEligibleLines: [],
      pendingVoucherCode: null, // held between validate and the line choice
      appointment: {},
      appointmentLinkedWithKtbPaotang: false,
      patient: {},
      appointmentId: null,
      treatment: null,
      treatmentList: [],
      overdueTreatmentList: [],
      appointmentDraft: null,
      aptDraftDisplay: {},
      nextVisitData: null,
      nextVisitSkipped: false,
      aptDate: "",

      receipt: {
        price: 0,
        amount: 0,
        Df: 0,
        Cf: 0,
        discountValue: 0,
        discountTotal: 0,
        paidDfDiscounted: 0,
        paidCfDiscounted: 0,
        discountPaidTotal: 0,
        finalDiscount: 0,
        overdue: 0,
        paid: 0,
        paidDf: 0,
        paidCf: 0,
        receiptItems: [],
        appointmentId: null,
        treatmentId: null,
        treatmentModifyDate: null,
      },
      index: null,
      tableBusy: false,
      addDeposit: false,
      depositAmount: 0,
      expandedItems: [],
      paymentError: null,
    };
  },
  created() {
    this.init();
  },
  mounted() {},
  computed: {
    ...mapGetters({
      getOrthoPackageList: "moduleTreatment/getOrthoPackageList",
      getOrthoPackageItemList: "moduleTreatment/getOrthoPackageItemList",
      getOrthoPackageItemBy: "moduleTreatment/getOrthoPackageItemBy",

      getDoctorList: "moduleUser/getDoctorList",
      getDoctorListBy: "moduleUser/getDoctorListBy",
      getUserInfo: "moduleUser/getUserInfo",
      getUserList: "moduleUser/getUserList",
      getUserListBy: "moduleUser/getUserListBy",

      authorization: "moduleUser/getAuthorization",
      viewAvatar: "moduleContent/viewAvatar",
      getAppointmentFormat: "moduleAppointment/getAppointmentFormat",
      getTreatmentBy: "moduleAppointment/getTreatmentBy",
      getTreatBy: "moduleAppointment/getTreatBy",
      getTimeDuration: "moduleAppointment/getTimeDuration",

      isManager: "moduleUser/isManager",
      isHeadCounter: "moduleUser/isHeadCounter",
      depositBalance: "moduleDeposit/getDepositBalance",
      hasDepositMethods: "moduleUser/hasDepositMethods",
      viewFile: "moduleContent/viewFile",
    }),
    patientFullNameTh() {
      const p = this.patient || {};
      return [p.titleNameTh, p.firstNameTh, p.lastNameTh]
        .filter(Boolean)
        .join(" ");
    },
    patientFullNameEn() {
      const p = this.patient || {};
      return [p.firstNameEn, p.lastNameEn].filter(Boolean).join(" ");
    },
    patientAbbreviation() {
      const p = this.patient || {};
      const f = p.firstNameEn ? p.firstNameEn.charAt(0) : "";
      const l = p.lastNameEn ? p.lastNameEn.charAt(0) : "";
      return `${f}${l}`;
    },
    patientGenderLabel() {
      const g = this.patient?.gender;
      if (!g) return "";
      const map = { M: "ชาย", F: "หญิง", male: "ชาย", female: "หญิง" };
      return map[g] || g;
    },
    patientAgeLabel() {
      const dob = this.patient?.dob;
      if (!dob) return "";
      const years = moment().diff(moment(dob, "YYYY-MM-DD"), "years");
      return `${years} ปี`;
    },
    patientPhoneLabel() {
      const m = this.patient?.mobile;
      return m ? formatPhoneForDisplay(m) : "";
    },
    treatmentItemList() {
      let allItems = [];
      let variants = ["lightBlue", "beige", "sky", "lightPurple", "skinTone"];
      let set = [...variants];
      let bgColor;
      this.treatmentList.forEach(appt => {
        let apptItems;
        if (appt.items.length === 0) {
          apptItems = [
            {
              appointmentId: appt.appointmentId,
              treatmentItemId: appt.id,
              creationDt: appt.creationDt,
              name: "-- ไม่มีรายการรักษา --",
              nameEn: "-- No treatment items --",
              emptyRow: true,
              _rowVariant: "dark",
            },
          ];
        } else {
          bgColor = set.shift();
          if (!set.length) {
            set = [...variants];
          }
          apptItems = appt.items.map(i => {
            const treatId = i.treatItems ? parseInt(i.treatItems) : null;
            const treatCateId = treatId
              ? this.getTreatBy(treatId, "id")?.cateId || null
              : null;
            return {
              appointmentId: appt.appointmentId,
              treatmentItemId: i.id,
              treatId,
              treatCateId,
              itemId: i.itemId || null,
              typeId: i.typeId || 1,
              discountId: null,
              price: i.price,
              discountMethod: 0,
              discountType: 0,
              discountPrice: 0,
              doctorDiscountPrice: 0,
              clinicDiscountPrice: 0,
              finalDf: 0,
              finalCf: 0,
              amount: i.price,
              finalDiscount: 0,
              overdue: 0,
              paid: 0,
              paidDf: 0,
              paidCf: 0,
              creationDt: appt.creationDt,
              name: i.name,
              nameEn: i.nameEn,
              actionUid: i.actionUid,
              doctor: this.getUserListBy(i.actionUid, "uid")?.fullName,
              doctorEn: this.getUserListBy(i.actionUid, "uid")?.fullNameEn,
              quantity: i.quantity || i.toothItems?.length || "",
              dfType: i.dfType,
              df: i.df,
              _rowVariant: bgColor,
            };
          });
          allItems.push(...apptItems);
        }
      });
      let overdueList = this.formatOverdueItems(this.overdueTreatmentList);
      allItems = overdueList.concat(allItems);
      return allItems;
    },
    sortedTreatmentItemList() {
      return this.treatmentItemList;
    },
    visibleItemCount() {
      return this.treatmentItemList.filter(i => !i.emptyRow && !i.overdueFlag)
        .length;
    },
    overdueState() {
      return this.receipt.overdue > 0;
    },
    isNhso() {
      return this.appointment?.extendedProps?.nhsoVisitNo > 0;
    },
    receiptDiscountOptions() {
      const eligibleItem = this.treatmentItemList.find(
        item => !item.emptyRow && !item.overdueFlag && item.typeId !== 2
      );
      if (!eligibleItem) return [];
      // The backend decides which discounts are offerable (threshold, usage, doctor,
      // scope) and returns the exact per-item breakdown it will persist.
      return this.discountSuggestions?.receiptSuggestions || [];
    },
    itemDiscountSuggestions() {
      return this.discountSuggestions?.itemSuggestions || {};
    },
    // A card of EITHER scope locks the manual receipt-level selector, for two
    // different reasons. A RECEIPT-scope code is overridden by the server anyway
    // (createReceipt.js:154-157), so offering the choice would be a lie. An
    // ITEM-scope code cannot be combined at all: eligibleLines' per-line deltas are
    // computed against an item-discount-only base and cannot see a receipt
    // discount, while the lines have already been rewritten by
    // applyReceiptDiscountToItems, so subtracting them double-counts. Computing it
    // properly needs the voucher's own discountId fed back through suggestDiscounts,
    // and the validate response deliberately withholds that id.
    voucherApplied() {
      return !!this.voucherCode;
    },
    // The server's own reason when the selected receipt discount would apply
    // nothing — empty whenever there is nothing to say.
    receiptDiscountNotice() {
      if (!this.receiptDiscountId) return "";
      return receiptDiscountNotice(
        this.receiptDiscountOptions.find(
          x => x.discountId === this.receiptDiscountId
        )
      );
    },
    receiptDiscountLabel() {
      if (!this.receiptDiscountId) return "";
      const d = this.receiptDiscountOptions.find(
        x => x.discountId === this.receiptDiscountId
      );
      if (!d) return "";
      // discountValue is the voucher's face value, not what actually applied (it can
      // be capped, or 0 for a split voucher). Show the voucher's own post-cap
      // contribution (appliedVoucherTotal), not appliedTotal which now includes the
      // stacked item discount.
      const applied = d.calculated?.appliedVoucherTotal ?? 0;
      const value = `-${applied.toLocaleString()} บาท`;
      return `ใช้ส่วนลด: ${d.name} (${value})`;
    },
    nextVisitMoment() {
      if (this.nextVisitData?.start) {
        return moment(this.nextVisitData.start).locale("th");
      }
      if (this.appointmentDraft?.startDt) {
        return moment(this.appointmentDraft.startDt).locale("th");
      }
      return null;
    },
    nextVisitDayLabel() {
      return this.nextVisitMoment ? this.nextVisitMoment.format("D") : "";
    },
    nextVisitMonthLabel() {
      return this.nextVisitMoment ? this.nextVisitMoment.format("MMM YY") : "";
    },
    nextVisitTimeLabel() {
      return this.nextVisitMoment
        ? this.nextVisitMoment.format("ddd H:mm")
        : "";
    },
    nextVisitDateLabel() {
      return this.nextVisitMoment
        ? this.nextVisitMoment.format("D MMM YY")
        : "";
    },
    nextVisitProcedureLabel() {
      const items = this.aptDraftDisplay?.treatmentItems;
      return items ? this.removeTags(items) : "";
    },
    nextVisitDurationLabel() {
      const min = this.nextVisitData?.extendedProps?.timeDuration;
      return min ? `${min} นาที` : "";
    },
  },
  watch: {
    nextVisitData() {
      if (this.nextVisitData === null) return;
      this.nextVisitSkipped = false;
      this.aptDate = moment(this.nextVisitData.start)
        .locale("th")
        .format("ddd D MMM YY / H:mm");
    },
  },
  methods: {
    ...mapActions({
      fetchQueueAppointmentById: "moduleAppointment/fetchQueueAppointmentById",
      fetchQueueAppointmentDraftByApptId:
        "moduleAppointment/fetchQueueAppointmentDraftByApptId",
      fetchTreatmentByApptId: "moduleTreatment/fetchTreatmentByApptId",
      fetchItemsPaymentByAptId: "moduleTreatment/fetchItemsPaymentByAptId",
      fetchOverdueTreatmentByPatientId:
        "moduleTreatment/fetchOverdueTreatmentByPatientId",
      fetchDepositSummary: "moduleDeposit/fetchDepositSummary",
      fetchPaymentMethodList: "moduleUser/fetchPaymentMethodList",
      suggestDiscountsAction: "moduleDiscount/suggestDiscounts",
      validateVoucherCode: "moduleVoucher/validateCode",
    }),
    init() {},
    show(appointment) {
      this.$bvModal.show("PaymentModal");
      let extendedProps = Object.assign(appointment.extendedProps);
      this.patient = Object.assign(extendedProps.patient);
      this.appointmentId = appointment.id;

      eventBus.$on("nextVisitCreated", e => {
        this.nextVisitData = e;
      });
      eventBus.$on("nextVisitEdited", e => {
        this.nextVisitData = e;
      });
      eventBus.$on(
        "deleteWaitingForPayment",
        this.handleDeleteWaitingForPayment
      );
      this.fetchAllItems();
    },
    async fetchAllItems() {
      this.isLoading = true;

      let clinicUrl = this.$route.params.clinicUrl;
      let branchUrl = this.$route.params.branchUrl;

      const { data } = await this.fetchQueueAppointmentById({
        id: this.appointmentId,
        clinicUrl: this.$route.params.clinicUrl,
        branchUrl: this.$route.params.branchUrl,
      });
      this.appointment = data;
      this.appointmentLinkedWithKtbPaotang = Boolean(
        this.appointment.extendedProps.linkedWithKtbPaotang
      );
      Promise.all([
        this.fetchItemsPaymentByAptId({
          appointmentId: this.appointmentId,
          clinicUrl: clinicUrl,
          branchUrl: branchUrl,
        }),
        this.fetchOverdueTreatmentByPatientId({
          patientId: this.patient.id,
          clinicUrl: clinicUrl,
          branchUrl: branchUrl,
        }),
        this.fetchQueueAppointmentDraftByApptId({
          id: this.appointmentId,
          clinicUrl: clinicUrl,
          branchUrl: branchUrl,
        }),
        this.fetchDepositSummary({
          clinicUrl: clinicUrl,
          branchUrl: branchUrl,
          patientId: this.patient.id,
        }).catch(() => {}),
        this.fetchPaymentMethodList({
          clinicUrl: clinicUrl,
          branchUrl: branchUrl,
        }).catch(() => {}),
      ])
        .then(async ([res1, res2, res3]) => {
          this.treatmentList = res1.data.treatments;
          this.overdueTreatmentList = res2.data;
          this.appointmentDraft = res3.data;

          if (res3.data) {
            this.getAppointmentDraftFormat(this.appointmentDraft);
          }
          await this.$nextTick();
          this.treatmentItemList.forEach(i => {
            this.calDiscount(i);
          });
          this.calTotalReceipt();

          // treatmentItemList is a computed rebuilt from treatmentList, so this
          // refetch discarded every line-level mutation the voucher made — while
          // voucherCode and friends survived. Leaving them set means the rail
          // shows the pre-voucher total under an "applied" card, the cashier
          // collects that, and the save books the post-voucher figure. Nothing
          // server-side rejects it.
          //
          // resetVoucherState, NOT clearVoucher: the lines above were just
          // rebuilt from fresh server data and re-costed by calDiscount, so
          // there is nothing left to unwind. clearVoucher would unwind anyway,
          // through restoreItemDiscountsAfterVoucherRemoved, against the
          // itemDiscountSuggestions of the PREVIOUS fetch — overwriting each
          // discounted line with figures computed for the pre-edit price, or,
          // when the edit recreated the rows under new ids, resetting the line
          // to full price and nulling its discountId. Either way the customer is
          // charged off numbers nobody chose. The fresh suggestions arrive on
          // the next line, after the unwind would already have happened.
          if (this.voucherCode) {
            this.resetVoucherState();
            this.voucherError = "รายการในใบเสร็จเปลี่ยนไป กรุณาสแกนบัตรอีกครั้ง";
          }

          // Same staleness, one field over: applyReceiptDiscountToItems' work is
          // gone with the rebuilt lines, but the id would still ship in the
          // receipt header and the server honours it — the rail (and therefore
          // the cash the cashier collects) would be short by that discount.
          // Unconditional, because this is true whether or not a card was also
          // applied. The select goes back to blank, which is what the lines now
          // say; the operator can re-pick it against the new item mix.
          this.receiptDiscountId = null;

          this.fetchDiscountSuggestions();
        })
        .catch(err => {
          console.error(err);
        })
        .finally(async () => {
          this.isLoading = false;
        });
    },
    openEventModal() {
      let appointment = null;
      if (this.appointmentDraft) {
        appointment = Object.assign(
          this.getAppointmentFormat(this.appointmentDraft)
        );
        appointment.id = null;
      }
      this.$parent.openEventModal(appointment, this.patient, "paymentModal");
    },
    openEventModalEdited(apt) {
      this.$parent.openEventModal(apt, null, "paymentModal");
    },
    addNextVisit() {
      this.nextVisitSkipped = false;
      this.openEventModal();
    },
    skipNextVisit() {
      this.nextVisitSkipped = true;
    },
    openProfileCustomerModal() {
      this.$parent.openProfileCustomerModal(null, this.patient, true);
    },
    openVerifyDeleteOverdueModal({ treatmentItemId }) {
      this.$refs.verifyDeleteOverdueModal.show({ id: treatmentItemId });
    },
    getAppointmentDraftFormat(appointmentDraft) {
      let { doctorUid, aptTreatItems, startDt, endDt, note } = appointmentDraft;

      this.aptDraftDisplay.doctor = this.getDoctorListBy(
        doctorUid,
        "uid"
      ).fullName;
      this.aptDraftDisplay.treatmentItems = aptTreatItems
        ?.map(i => this.getTreatmentBy(i, "id").nameTh)
        .join(", ");
      this.aptDraftDisplay.duration = moment(endDt).diff(
        moment(startDt),
        "minutes"
      );
      const daysDiff = moment(startDt).diff(moment(), "days", true);
      this.aptDraftDisplay.inDays = Math.ceil(daysDiff);
      this.aptDraftDisplay.note = note;
    },

    async fetchDiscountSuggestions() {
      try {
        const items = this.treatmentItemList
          .filter(i => !i.emptyRow && !i.overdueFlag && i.typeId !== 2)
          .map(i => {
            const doctor = this.getUserListBy(i.actionUid, "uid");
            return {
              treatmentItemId: i.treatmentItemId,
              treatId: i.treatId || null,
              treatCateId: i.treatCateId || null,
              itemId: i.itemId || null,
              typeId: i.typeId || 1,
              price: i.price,
              df: i.df,
              dfType: i.dfType,
              quantity: i.quantity || 1,
              // Send the line's chosen item discount so the backend layers the
              // receipt voucher on top of it (on-top stacking); without it the BE
              // would compute the voucher off full price and preview != save.
              discountId: i.discountId || null,
              doctorId: doctor?.uid || null,
            };
          });

        if (items.length === 0) return;

        const res = await this.suggestDiscountsAction({
          clinicUrl: this.$route.params.clinicUrl,
          branchUrl: this.$route.params.branchUrl,
          treatmentItems: items,
          patientId: this.patient?.id || null,
        });
        this.discountSuggestions = res || {};
      } catch {
        this.discountSuggestions = {};
      }
    },
    onDiscountSelected(item, discount) {
      if (item?.overdueFlag) return;
      if (discount) {
        const c = discount.calculated || {};
        item.discountId = discount.discountId;
        item.amount = c.amount;
        item.finalDiscount = c.discountAmount;
        item.paid = c.amount;
        item.overdue = 0;
        if (c.finalDf !== undefined) item.finalDf = c.finalDf;
        if (c.finalCf !== undefined) item.finalCf = c.finalCf;
        if (c.discountMethod !== undefined)
          item.discountMethod = c.discountMethod;
        if (c.discountType !== undefined) item.discountType = c.discountType;
        if (c.doctorDiscountAmount !== undefined)
          item.doctorDiscountAmount = c.doctorDiscountAmount;
        if (c.clinicDiscountAmount !== undefined)
          item.clinicDiscountAmount = c.clinicDiscountAmount;
        if (c.doctorDiscountPrice !== undefined)
          item.doctorDiscountPrice = c.doctorDiscountPrice;
        if (c.clinicDiscountPrice !== undefined)
          item.clinicDiscountPrice = c.clinicDiscountPrice;
      } else {
        item.discountId = null;
        item.amount = parseFloat(item.price) || 0;
        item.finalDiscount = 0;
        item.paid = item.amount;
        item.overdue = 0;
        if (item.dfType === 1) {
          const fixedDf = (parseFloat(item.df) || 0) * (item.quantity || 1);
          item.finalDf = +Math.min(fixedDf, item.amount).toFixed(2);
        } else {
          item.finalDf = +(
            item.amount *
            ((parseFloat(item.df) || 0) / 100)
          ).toFixed(2);
        }
        item.finalCf = +(item.amount - item.finalDf).toFixed(2);
      }
      this.calPaidItem(item);
      this.calTotalReceipt();
      // The item discount just changed, so any voucher preview is now stale (the BE
      // layers the voucher on top of the item discountId). Refresh suggestions and, if
      // a voucher is active, re-apply it from the fresh breakdown so the on-screen
      // numbers match what save will send.
      this.refreshSuggestionsAndReapplyVoucher();
    },
    async refreshSuggestionsAndReapplyVoucher() {
      await this.fetchDiscountSuggestions();
      // Was an early `if (!this.receiptDiscountId) return;`. Turned into a block so
      // the voucher-code guard below is reached on EVERY item-discount change, not
      // only when a receipt-level discount happens to be selected. Everything inside
      // — calTotalReceipt included — still runs exactly when it ran before.
      if (this.receiptDiscountId) {
        const discount = this.receiptDiscountOptions.find(
          d => d.discountId === this.receiptDiscountId
        );
        if (discount) {
          this.applyReceiptDiscountToItems(discount);
        } else {
          // The voucher is no longer offered for the new item mix; drop it but keep the
          // item-level discount already applied above.
          this.receiptDiscountId = null;
        }
        this.calTotalReceipt();
      }

      // A validated code was checked against the previous line set. Rather than
      // silently save something the server will refuse, drop it and make the
      // operator re-scan against what is actually on the receipt now.
      if (this.voucherCode) {
        this.clearVoucher();
        this.voucherError = "รายการในใบเสร็จเปลี่ยนไป กรุณาสแกนบัตรอีกครั้ง";
      }
    },
    // Per-line fields match what suggestDiscounts sends (see
    // fetchDiscountSuggestions): discountId must travel so the backend computes
    // the voucher off the post-item-discount net, or preview and save disagree.
    // doctorId is resolved from actionUid the same way — the campaign's doctor
    // restriction filters lines on it, so a null there rejects an otherwise valid
    // card.
    //
    // The SELECTION is deliberately different: fetchDiscountSuggestions drops
    // typeId === 2 (ortho), this does not. computeReceiptPreview's doctor check
    // runs .every over the whole submission, mirroring createReceipt.js:742, so
    // hiding the ortho lines here would let a doctor-restricted card preview as
    // eligible and then fail at save — with the cashier already told a total.
    // Do NOT "make it consistent" by adding the filter.
    voucherTreatmentItems() {
      return this.treatmentItemList
        .filter(i => !i.emptyRow && !i.overdueFlag)
        .map(i => ({
          treatmentItemId: i.treatmentItemId,
          treatId: i.treatId || null,
          treatCateId: i.treatCateId || null,
          itemId: i.itemId || null,
          // VoucherValidateInput declares typeId and dfType as non-nullable
          // integers, unlike its nullable treatId/itemId/doctorId/discountId
          // neighbours. treatment_item.df_type is genuinely NULL on real rows
          // (tests/e2e/helpers/treatment.ts:33-40 documents exactly that), and a
          // null here makes oas-tools 400 the request — the operator would see
          // only "ตรวจสอบบัตรไม่สำเร็จ" and have no way to scan the card at all.
          // typeId || 1 is the fallback fetchDiscountSuggestions already uses;
          // dfType 0 (= percentage) is how restoreItemDiscountsAfterVoucherRemoved
          // already reads a null dfType, so this matches the file's own semantics.
          typeId: i.typeId || 1,
          price: Number(i.price) || 0,
          df: Number(i.df) || 0,
          dfType: Number(i.dfType) || 0,
          quantity: Number(i.quantity) || 1,
          doctorId: this.getUserListBy(i.actionUid, "uid")?.uid || null,
          discountId: i.discountId || null,
        }));
    },

    async onVoucherSubmit(code) {
      this.voucherBusy = true;
      this.voucherError = "";
      // A previous scan may have left the line picker open. Nothing about that
      // scan survives this one — clearing here stops a later pick from
      // resurrecting the old code through pendingVoucherCode.
      this.voucherLinesOpen = false;
      this.voucherEligibleLines = [];
      this.pendingVoucherCode = null;
      try {
        const result = await this.validateVoucherCode({
          clinicUrl: this.$route.params.clinicUrl,
          branchUrl: this.$route.params.branchUrl,
          code,
          patientId: this.patient?.id || null,
          treatmentItems: this.voucherTreatmentItems(),
        });

        // validate always returns 200, even on rejection — it is called on every
        // scan and the message belongs inline, not in an error toast.
        if (!result.ok) {
          this.voucherError = result.message || "ใช้บัตรใบนี้ไม่ได้";
          return;
        }

        if (result.scope === "ITEM") {
          // The eligibleLines deltas are computed against an item-discount-only
          // base and cannot see a manual receipt discount, while the lines have
          // already been rewritten by applyReceiptDiscountToItems — subtracting
          // here double-counts. Refuse rather than show a figure the save will
          // not match. Refuse, NOT silently clear: the operator chose that
          // discount deliberately and it may be the larger of the two.
          if (this.receiptDiscountId) {
            this.voucherError =
              "กรุณาเอาส่วนลดระดับใบเสร็จออกก่อน จึงจะใช้บัตรกำนัลแบบรายหัตถการได้";
            return;
          }

          const lines = result.eligibleLines || [];
          if (!lines.length) {
            this.voucherError = "ไม่มีรายการในใบเสร็จนี้ที่ใช้บัตรใบนี้ได้";
            return;
          }
          this.voucherInfo = result;
          this.voucherEligibleLines = lines;
          if (lines.length === 1) {
            // Still the operator's choice in spirit, but there is nothing to
            // choose between — take it and show what was picked.
            this.applyVoucherLine(lines[0].treatmentItemId, code);
          } else {
            this.pendingVoucherCode = code;
            this.voucherLinesOpen = true;
          }
          return;
        }

        // ok:true means the CODE is valid, not that this receipt can accept it.
        // receiptPreview is the only signal the screen gets before the save
        // refuses outright — the ITEM path screens on eligibleLines for the
        // same reason. Runs BEFORE the unwind below: refusing a card must not
        // clear the operator's existing receipt discount.
        if (result.receiptPreview && !result.receiptPreview.eligible) {
          this.voucherError =
            result.receiptPreview.message || "ใช้บัตรใบนี้ไม่ได้กับรายการในใบเสร็จนี้";
          return;
        }

        // Without the server's per-line allocation this screen cannot make the
        // till agree with the save: the rail would stay at the pre-voucher total,
        // PaymentMethod.vue would make the cashier collect it, and the save would
        // record less. Refuse the card rather than overcharge. Unreachable against
        // a backend carrying the allocation — the two ship together — so it reuses
        // the generic validate-failed message rather than inventing a new one.
        if (!result.receiptPreview?.lines?.length) {
          this.voucherError = "ตรวจสอบบัตรไม่สำเร็จ";
          return;
        }

        // RECEIPT scope: allocated across every eligible line server-side.
        //
        // The server discards a client-sent receiptDiscountId whenever a code is
        // presented (receipt_service/createReceipt.js:154-157, "a redeemed code
        // always wins"). Leaving the manual selection on screen would show the
        // operator a total the receipt will not carry. Unwound BEFORE the code is
        // recorded, so the line totals are back to item-discount-only before
        // anything depends on voucherCode. ITEM scope never reaches here — it
        // redeems on the item path and does not override the receipt discount.
        if (this.receiptDiscountId) {
          // Not a bare assignment: this path carries the on-top unwind that
          // restores each line's own item discount instead of blanket-resetting to
          // full price. Its else branch nulls receiptDiscountId itself, so the
          // assignment below is belt-and-braces, not the thing doing the work.
          this.onReceiptDiscountSelected(null);
          this.receiptDiscountId = null;
        }
        this.voucherCode = code;
        this.voucherLineId = null;
        this.voucherInfo = result;
        this.applyVoucherAllocationToItems(result.receiptPreview.lines);
      } catch (error) {
        this.voucherError =
          error?.response?.data?.message || "ตรวจสอบบัตรไม่สำเร็จ";
      } finally {
        this.voucherBusy = false;
      }
    },

    // The receipt header (amount, paid, finalDiscount, overdue) is aggregated from
    // treatmentItemList by calTotalReceipt() and sent whole, and PaymentMethod.vue
    // makes the cashier collect exactly receipt.paid. So the voucher has to reach
    // these lines, or the customer pays the pre-voucher price and the difference is
    // booked nowhere. Same mechanism and the same fields applyReceiptDiscountToItems
    // uses for a manual receipt discount.
    //
    // The difference: those are absolute per-line values, these are DELTAS on top of
    // each line's current item-discounted net, so they are subtracted rather than
    // assigned. The split is never derived here — which line a voucher lands on and
    // how much of it falls on DF vs CF is the server's decision (layerReceiptDiscount),
    // and it now returns it. Excluded lines are ABSENT from `lines`, not present with
    // zero, so a line with no entry is simply left alone.
    applyVoucherAllocationToItems(lines) {
      const byId = new Map((lines || []).map(l => [l.treatmentItemId, l]));

      this.treatmentItemList.forEach(item => {
        if (item.emptyRow || item.overdueFlag || item.typeId === 2) return;
        const share = byId.get(item.treatmentItemId);
        if (!share) return;

        const voucherDf = Number(share.voucherDf) || 0;
        const voucherCf = Number(share.voucherCf) || 0;
        const voucherTotal = Number(share.voucherTotal) || 0;

        // No clamp here, on purpose. For receiptPreview.lines,
        // layerReceiptDiscount caps each side at that line's own net DF/CF. The
        // ITEM path is different: voucherDf/voucherCf come from independently
        // rounded calculateDiscount differences, so a side can legitimately land
        // at -0.01 and the two still sum to the right voucherTotal. Clamping at
        // zero would silently drop that satang from the DF/CF split the receipt
        // header carries, so the arithmetic must be left exactly as the server
        // computed it. Do NOT add a Math.max here.
        item.finalDf = +((Number(item.finalDf) || 0) - voucherDf).toFixed(2);
        item.finalCf = +((Number(item.finalCf) || 0) - voucherCf).toFixed(2);
        item.amount = +((Number(item.amount) || 0) - voucherTotal).toFixed(2);
        item.finalDiscount = +(
          (Number(item.finalDiscount) || 0) + voucherTotal
        ).toFixed(2);
        item.paid = item.amount;
        item.overdue = 0;

        if (item.amount > 0) {
          this.calPaidItem(item);
        } else {
          // A voucher can cover a whole line. calPaidItem's paidDf is
          // finalDf * (paid / amount), which is NaN at amount 0 — the same reason
          // createReceipt.js:927 guards its own paidDf on amount > 0.
          item.paid = 0;
          item.overdue = 0;
          item.paidDf = 0;
          item.paidCf = 0;
        }
      });

      this.calTotalReceipt();
    },

    applyVoucherLine(treatmentItemId, code) {
      // A second click while the picker closes: the first application already
      // consumed the list, so there is nothing left to pick and the card is
      // correctly applied. Returning quietly beats writing a false error under a
      // card that worked. The list is never empty on a genuine first call — both
      // callers populate it immediately before.
      if (!this.voucherEligibleLines.length) return;

      // Captured before voucherEligibleLines is emptied below. Only the CHOSEN
      // line is applied — the other eligible lines are offers, not applications,
      // and one code discounts exactly one line.
      const chosen = this.voucherEligibleLines.find(
        l => l.treatmentItemId === treatmentItemId
      );

      // Without the server's split for that line the screen cannot make the till
      // agree with the save, and the cashier would collect the pre-voucher total.
      // Refuse rather than overcharge — the same rule the receipt-scope path
      // applies. Unreachable (the picker's rows ARE these lines), so it reuses the
      // generic validate-failed message rather than inventing a new one.
      if (!chosen || !(Number(chosen.voucherTotal) > 0)) {
        this.voucherError = "ตรวจสอบบัตรไม่สำเร็จ";
        this.pendingVoucherCode = null;
        this.voucherLinesOpen = false;
        this.voucherEligibleLines = [];
        return;
      }

      this.voucherCode = code || this.pendingVoucherCode;
      this.voucherLineId = treatmentItemId;
      this.pendingVoucherCode = null;
      this.voucherLinesOpen = false;
      this.voucherEligibleLines = [];
      // eligibleLines carries voucherDf/voucherCf/voucherTotal in the same field
      // names and with the same delta semantics as receiptPreview.lines, so an
      // item-scope card goes through the SAME apply path as a receipt-scope one.
      this.applyVoucherAllocationToItems([chosen]);
    },

    onVoucherLinePicked(treatmentItemId) {
      this.applyVoucherLine(treatmentItemId);
    },

    // The operator dismissed the picker without choosing: nothing was applied, so
    // no residue may be left behind — that residue is what made a second scan
    // mis-attribute a card. b-modal also emits hide when the picker is closed
    // programmatically (a line was picked, or a new scan reset it); those paths
    // set their own state and must keep voucherInfo, so the already-closed guard
    // makes this a no-op for them.
    closeVoucherLinePicker() {
      if (!this.voucherLinesOpen) return;
      this.voucherLinesOpen = false;
      this.voucherEligibleLines = [];
      this.pendingVoucherCode = null;
      this.voucherInfo = null;
    },

    // The voucher fields and NOTHING else. Split out of clearVoucher for callers
    // that hold lines the voucher never touched — fetchAllItems rebuilds
    // treatmentItemList from the server before it gets here — where running the
    // unwind would corrupt those lines against suggestions from an older fetch.
    // Anyone whose lines still carry an applied allocation wants clearVoucher.
    resetVoucherState() {
      this.voucherCode = null;
      this.voucherLineId = null;
      this.voucherInfo = null;
      this.voucherError = "";
      this.voucherEligibleLines = [];
      this.voucherLinesOpen = false;
      this.pendingVoucherCode = null;
    },

    clearVoucher() {
      // Both scopes reach the lines through applyVoucherAllocationToItems, so any
      // applied code has something to unwind. Also runs from defaultValue() on
      // every modal close: wasApplied is true there whenever a card was applied
      // and the modal is dismissed without saving, and the unwind is harmless
      // only because defaultValue has already emptied treatmentList two lines
      // earlier, so the restore iterates nothing.
      const wasApplied = this.voucherApplied;
      this.resetVoucherState();
      if (wasApplied) {
        // On-top unwind: every line falls back to its OWN item discount, never to
        // full price — the same path a removed manual receipt discount takes.
        this.restoreItemDiscountsAfterVoucherRemoved();
        // That restore knows only about item discounts, so a manual receipt
        // discount would have to go back on top of it. Unreachable while the two
        // cannot coexist — a receipt-scope card displaces the selection and an
        // item-scope card is refused against it — but kept as the correct
        // behaviour if that lock is ever loosened, since a silent strip here would
        // leave receiptDiscountId in the payload with its value off the lines.
        // Same re-apply shape refreshSuggestionsAndReapplyVoucher uses.
        if (this.receiptDiscountId) {
          const discount = this.receiptDiscountOptions.find(
            d => d.discountId === this.receiptDiscountId
          );
          if (discount) {
            this.applyReceiptDiscountToItems(discount);
          } else {
            // No longer offered for this item mix: drop it rather than send an id
            // the screen did not apply — the server would honour it and the header
            // this modal sends would be short by that discount.
            this.receiptDiscountId = null;
          }
        }
        this.calTotalReceipt();
      }
    },

    onVoucherDecoded(code) {
      this.voucherCameraOpen = false;
      this.onVoucherSubmit(code);
    },

    // No thaiDate filter exists in this file; format inline rather than invent one.
    formatVoucherValidTo(value) {
      return value ? moment(value).format("DD/MM/YYYY") : "";
    },

    onReceiptDiscountSelected(discountId) {
      if (discountId) {
        const discount = this.receiptDiscountOptions.find(
          d => d.discountId === discountId
        );
        if (discount) {
          this.applyReceiptDiscountToItems(discount);
        }
      } else {
        this.receiptDiscountId = null;
        // On-top: clearing the voucher must only remove the voucher's contribution and
        // fall back to each line's own item discount — NOT blanket-reset to full price,
        // which would silently discard still-valid per-line item discounts.
        this.restoreItemDiscountsAfterVoucherRemoved();
      }
      this.calTotalReceipt();
    },
    applyReceiptDiscountToItems(discount) {
      // The backend already computed every per-item value for this discount and will
      // recompute the same values on save. Apply its breakdown verbatim — re-deriving it
      // here would be a second implementation of the allocator that can drift, and the
      // totals this modal sends are persisted as the receipt header as-is.
      const byId = new Map(
        (discount.calculated?.items || []).map(i => [i.treatmentItemId, i])
      );

      this.treatmentItemList.forEach(item => {
        if (item.emptyRow || item.overdueFlag || item.typeId === 2) return;
        const calc = byId.get(item.treatmentItemId);
        if (!calc) return;

        // Keep the line's own item discountId — the BE breakdown here is already
        // item+voucher combined, and on save the BE re-derives the voucher from this
        // line's discountId. Nulling it would make save stack off full price → preview
        // != save.
        item.finalDiscount = calc.finalDiscount;
        item.amount = calc.amount;
        item.paid = calc.amount;
        item.overdue = 0;
        item.finalDf = calc.finalDf;
        item.finalCf = calc.finalCf;

        this.calPaidItem(item);
      });
    },
    restoreItemDiscountsAfterVoucherRemoved() {
      // The voucher was just cleared. For each line, revert to the item-discount-only
      // state: if the line kept its own item discount, re-apply THAT discount's breakdown
      // from the current item-level suggestions (mirrors onDiscountSelected's discount
      // branch); otherwise fall back to full price. Never re-fetch suggestions here — this
      // path runs off the suggestions already in memory, so there is no re-fetch loop.
      this.treatmentItemList.forEach(item => {
        if (item.emptyRow || item.overdueFlag || item.typeId === 2) return;

        const suggestion = item.discountId
          ? (this.itemDiscountSuggestions[item.treatmentItemId] || []).find(
              s => s.discountId === item.discountId
            )
          : null;

        if (suggestion) {
          const c = suggestion.calculated || {};
          item.discountId = suggestion.discountId;
          item.amount = c.amount;
          item.finalDiscount = c.discountAmount;
          item.paid = c.amount;
          item.overdue = 0;
          if (c.finalDf !== undefined) item.finalDf = c.finalDf;
          if (c.finalCf !== undefined) item.finalCf = c.finalCf;
          if (c.discountMethod !== undefined) item.discountMethod = c.discountMethod;
          if (c.discountType !== undefined) item.discountType = c.discountType;
          if (c.doctorDiscountAmount !== undefined)
            item.doctorDiscountAmount = c.doctorDiscountAmount;
          if (c.clinicDiscountAmount !== undefined)
            item.clinicDiscountAmount = c.clinicDiscountAmount;
          if (c.doctorDiscountPrice !== undefined)
            item.doctorDiscountPrice = c.doctorDiscountPrice;
          if (c.clinicDiscountPrice !== undefined)
            item.clinicDiscountPrice = c.clinicDiscountPrice;
        } else {
          // No item discount on this line, or its suggestion is no longer offered:
          // reset this line to full price.
          item.discountId = null;
          item.amount = parseFloat(item.price) || 0;
          item.finalDiscount = 0;
          item.paid = item.amount;
          item.overdue = 0;
          if (item.dfType === 1) {
            const fixedDf = (parseFloat(item.df) || 0) * (item.quantity || 1);
            item.finalDf = +Math.min(fixedDf, item.amount).toFixed(2);
          } else {
            item.finalDf = +(item.amount * ((parseFloat(item.df) || 0) / 100)).toFixed(2);
          }
          item.finalCf = +(item.amount - item.finalDf).toFixed(2);
        }

        this.calPaidItem(item);
      });
    },
    calDiscount(i) {
      let total, finalDf, finalCf;
      if (i.discountMethod === 0 && i.dfType === 0 && i.discountType === 0) {
        this.setMinMax(i, "discountPrice", 0, 100);
        total = i.price * ((100 - i.discountPrice) / 100);
        finalDf = i.price * (i.df / 100) * ((100 - i.discountPrice) / 100);
        finalCf = total - finalDf;
      } else if (
        i.discountMethod === 0 &&
        i.dfType === 0 &&
        i.discountType === 1
      ) {
        this.setMinMax(i, "discountPrice", 0, i.price);
        total = i.price - i.discountPrice;
        finalDf = total * (i.df / 100);
        finalCf = total - finalDf;
      } else if (
        i.discountMethod === 0 &&
        i.dfType === 1 &&
        i.discountType === 0
      ) {
        this.setMinMax(i, "discountPrice", 0, 100);
        total = i.price * ((100 - i.discountPrice) / 100);
        finalDf = i.df * (i.quantity || 1) * ((100 - i.discountPrice) / 100);
        finalCf = total - finalDf;
      } else if (
        i.discountMethod === 0 &&
        i.dfType === 1 &&
        i.discountType === 1
      ) {
        console.warn("**DF fixed / Discount fixed cannot calculate discount**");
      } else if (
        i.discountMethod === 1 &&
        i.dfType === 0 &&
        i.discountType === 0
      ) {
        // % of the WHOLE price, capped at each side's share (excess dropped)
        const fullDf = i.price * (i.df / 100);
        const fullCf = i.price * ((100 - i.df) / 100);
        this.setMinMax(i, "doctorDiscountPrice", 0, 100);
        this.setMinMax(i, "clinicDiscountPrice", 0, 100);
        i.doctorDiscountAmount = Math.min(
          i.price * (i.doctorDiscountPrice / 100),
          fullDf
        );
        i.clinicDiscountAmount = Math.min(
          i.price * (i.clinicDiscountPrice / 100),
          fullCf
        );
        finalDf = fullDf - i.doctorDiscountAmount;
        finalCf = fullCf - i.clinicDiscountAmount;
        total = finalDf + finalCf;
      } else if (
        i.discountMethod === 1 &&
        i.dfType === 0 &&
        i.discountType === 1
      ) {
        let fullDf = i.price * (i.df / 100);
        let fullCf = i.price * ((100 - i.df) / 100);

        this.setMinMax(i, "doctorDiscountPrice", 0, fullDf);
        this.setMinMax(i, "clinicDiscountPrice", 0, fullCf);

        finalDf = fullDf - i.doctorDiscountPrice;
        finalCf = fullCf - i.clinicDiscountPrice;
        total = finalDf + finalCf;
        i.doctorDiscountAmount = i.doctorDiscountPrice;
        i.clinicDiscountAmount = i.clinicDiscountPrice;
      } else if (
        i.discountMethod === 1 &&
        i.dfType === 1 &&
        i.discountType === 0
      ) {
        // % of the WHOLE price, capped at each side's share (excess dropped)
        let fullDf = i.df * (i.quantity || 1);
        let fullCf = i.price - fullDf;
        if (fullDf > i.price) {
          fullDf = i.price;
          fullCf = 0;
        }
        this.setMinMax(i, "doctorDiscountPrice", 0, 100);
        this.setMinMax(i, "clinicDiscountPrice", 0, 100);
        i.doctorDiscountAmount = Math.min(
          i.price * (i.doctorDiscountPrice / 100),
          fullDf
        );
        i.clinicDiscountAmount = Math.min(
          i.price * (i.clinicDiscountPrice / 100),
          fullCf
        );
        finalDf = fullDf - i.doctorDiscountAmount;
        finalCf = fullCf - i.clinicDiscountAmount;
        total = finalDf + finalCf;
      } else if (
        i.discountMethod === 1 &&
        i.dfType === 1 &&
        i.discountType === 1
      ) {
        let fullCf = i.price - i.df * (i.quantity || 1);
        this.setMinMax(i, "doctorDiscountPrice", 0, i.df * (i.quantity || 1));
        this.setMinMax(i, "clinicDiscountPrice", 0, fullCf);

        finalDf = i.df * (i.quantity || 1) - i.doctorDiscountPrice;
        finalCf = fullCf - i.clinicDiscountPrice;
        total = finalDf + finalCf;
      } else {
        finalDf = 0;
        finalCf = i.price;
        total = i.price;
      }
      i.finalDf = finalDf;
      i.finalCf = finalCf;
      i.amount = total;
      i.finalDiscount = i.price - total;
      if (!i.overdueFlag) {
        i.paid = total;
      } else {
        i.paid = i.remained;
      }

      this.calPaidItem(i);
      this.calTotalReceipt();
    },
    calPaidItem(i) {
      if (i.overdueFlag) {
        this.setMinMax(i, "paid", 0, i.remained);
        i.overdue = i.remained - i.paid;
      } else {
        this.setMinMax(i, "paid", 0, i.amount);
        i.overdue = i.amount - i.paid;
      }
      let paidDf, paidCf;
      let num = i.finalDf * (i.paid / i.amount);
      paidDf = +parseFloat(num).toFixed(2);
      paidCf = i.paid - paidDf;
      i.paidDf = paidDf;
      i.paidCf = paidCf;

      this.calTotalReceipt();
    },

    calDepositAmount() {
      this.depositAmount = Math.max(0, parseFloat(this.depositAmount) || 0);
      this.calTotalReceipt();
    },

    calTotalReceipt() {
      Object.assign(this.receipt, {
        price: 0,
        amount: 0,
        Df: 0,
        Cf: 0,
        discountValue: 0,
        discountTotal: 0,
        paidDfDiscounted: 0,
        paidCfDiscounted: 0,
        discountPaidTotal: 0,
        finalDiscount: 0,
        overdue: 0,
        paid: 0,
        paidDf: 0,
        paidCf: 0,
        appointmentId: this.appointmentId,
        treatmentId: this.treatmentList[0]?.id || null,
        treatmentModifyDate: this.treatmentList[0]?.modifyDt || null,
      });
      function formatNumber(value) {
        return +parseFloat(value).toFixed(2);
      }
      this.treatmentItemList.forEach(res => {
        if (res.overdueFlag) {
          this.receipt.amount += res.remained ? formatNumber(res.remained) : 0;
        } else {
          this.receipt.amount += res.amount ? formatNumber(res.amount) : 0;
        }

        this.receipt.Df += res.finalDf ? formatNumber(res.finalDf) : 0;
        this.receipt.Cf += res.finalCf ? formatNumber(res.finalCf) : 0;
        this.receipt.price += res.price ? formatNumber(res.price) : 0;
        this.receipt.overdue += res.overdue ? formatNumber(res.overdue) : 0;
        this.receipt.paid += res.paid ? formatNumber(res.paid) : 0;
        this.receipt.paidDf += res.paidDf ? formatNumber(res.paidDf) : 0;
        this.receipt.paidCf += res.paidCf ? formatNumber(res.paidCf) : 0;
        if (!res.overdueFlag) {
          this.receipt.finalDiscount += res.finalDiscount
            ? formatNumber(res.finalDiscount)
            : 0;
        }
      });

      if (this.addDeposit && this.depositAmount > 0) {
        this.receipt.paid += formatNumber(this.depositAmount);
      }
    },

    async openPaymentMethodModal() {
      const prepareReceiptItems = () => {
        this.receipt.receiptItems = [];
        this.receipt.receiptItems = [...this.treatmentItemList];
        this.receipt.receiptDiscountId = this.receiptDiscountId || null;
        // Write-only. The server resolves the code to its hidden discount,
        // consumes it atomically, and persists neither field on the receipt row.
        // voucherLineId is REQUIRED for an ITEM-scope code and REJECTED for a
        // RECEIPT-scope one, so it is null unless a line was actually chosen.
        this.receipt.voucherCode = this.voucherCode || null;
        // `?? null`, not `|| null`: a 0 id would coerce to null and an ITEM code
        // would be rejected as if no line had been chosen (cf. utils/isEmpty.js).
        this.receipt.voucherLineId = this.voucherLineId ?? null;

        if (this.addDeposit && this.depositAmount > 0) {
          this.receipt.receiptItems.push({
            type: "DEPOSIT",
            name: "เงินมัดจำ",
            nameEn: "Deposit",
            amount: this.depositAmount,
            paid: this.depositAmount,
            price: this.depositAmount,
            overdue: 0,
          });
        }
      };

      if (this.receipt.overdue != 0) {
        await this.$refs.Dialog.showAlertConfirm(
          `มีรายการค้างชำระ ${this.receipt.overdue} บาท`,
          "คุณต้องการดำเนินการชำะเงินต่อ"
        ).then(res => {
          if (res.isConfirmed) {
            prepareReceiptItems();
            this.$refs.paymentMethodModal.show({
              receipt: this.receipt,
              patient: this.patient,
              appointment: this.appointment,
            });
          }
        });
      } else {
        prepareReceiptItems();
        this.$refs.paymentMethodModal.show({
          receipt: this.receipt,
          patient: this.patient,
          appointment: this.appointment,
        });
      }
    },
    openPaymentHistoryModal(appointmentId) {
      this.$parent.openPaymentHistoryModal(this.appointment);
    },
    openPaymentReceiptModal(receipt) {
      this.$emit("openPaymentReceiptModal", receipt, this.treatment);
    },
    afterDelete() {
      this.fetchAllItems();
      this.$refs.Dialog.showToast("success", "ลบรายการค้างชำระเรียบร้อย");
    },

    toggleItemDiscount(item) {
      const id = item.treatmentItemId;
      const idx = this.expandedItems.indexOf(id);
      if (idx >= 0) {
        this.expandedItems.splice(idx, 1);
      } else {
        this.expandedItems.push(id);
      }
    },
    isItemDiscountOpen(item) {
      return this.expandedItems.includes(item.treatmentItemId);
    },
    getItemCardClass(item) {
      const variantClassMap = {
        lightBlue: "payment-item-card--lightblue",
        beige: "payment-item-card--beige",
        sky: "payment-item-card--sky",
        lightPurple: "payment-item-card--lightpurple",
        skinTone: "payment-item-card--skintone",
        danger: "payment-item-card--danger",
        dark: "payment-item-card--dark",
      };
      return variantClassMap[item._rowVariant] || "";
    },

    defaultValue() {
      eventBus.$off("nextVisitCreated");
      eventBus.$off("nextVisitEdited");
      eventBus.$off("deleteWaitingForPayment");
      this.treatmentList = [];
      this.nextVisitData = null;
      this.nextVisitSkipped = false;
      this.aptDraftDisplay = {};
      this.appointment = {};
      this.appointmentLinkedWithKtbPaotang = false;
      this.addDeposit = false;
      this.depositAmount = 0;
      this.expandedItems = [];
      this.paymentError = null;
      this.receiptDiscountId = null;
      this.clearVoucher();
      Object.assign(this.receipt, {
        price: 0,
        amount: 0,
        Df: 0,
        Cf: 0,
        discountValue: 0,
        discountTotal: 0,
        paidDfDiscounted: 0,
        paidCfDiscounted: 0,
        discountPaidTotal: 0,
        finalDiscount: 0,
        overdue: 0,
        paid: 0,
        paidDf: 0,
        paidCf: 0,
        receiptItems: [],
        appointmentId: null,
        treatmentId: null,
        treatmentModifyDate: null,
      });
    },

    receiptDiscountOptionLabel,
    formatCurrency(value) {
      return this.$utils.formatCurrency(value);
    },
    setMinMax(obj, key, min, max) {
      let num = Math.min(Math.max(obj[key], min), max) || 0;
      obj[key] = +parseFloat(num).toFixed(2);
    },
    formatDate(date) {
      if (date) {
        return moment(date).locale("th").format("D MMM YY");
      } else {
        return "";
      }
    },
    formatOverdueItems(arr) {
      return arr.map(i => {
        const lri = i.lastReceiptItem;
        return {
          appointmentId: null,
          treatmentItemId: i.id,
          price: i.price,
          discountMethod: lri?.discountMethod,
          discountType: lri?.discountType,
          discountPrice: lri?.discountPrice,
          doctorDiscountPrice: lri?.doctorDiscountPrice,
          clinicDiscountPrice: lri?.clinicDiscountPrice,
          finalDf: lri?.finalDf,
          finalCf: lri?.finalCf,
          amount: lri?.amount,
          finalDiscount: lri?.finalDiscount,
          // BE-computed outstanding is the true current remain. Fall back to
          // lastReceiptItem.overdue for old API responses during deploy gap.
          remained: i.outstanding ?? lri?.overdue,
          paid: i.paid,
          paidDf: lri?.paidDf,
          paidCf: lri?.paidCf,
          creationDt: lri?.creationDt,
          name: i.name,
          actionUid: i.actionUid,
          doctor: this.getUserListBy(i.actionUid, "uid")?.fullName,
          quantity: i.quantity,
          dfType: i.dfType,
          df: i.df,
          _rowVariant: "danger",
          overdueFlag: i.overdueFlag,
        };
      });
    },
    removeTags(strWithTags) {
      if (!strWithTags) return "-";
      else strWithTags = strWithTags.toString();

      return strWithTags.replace(/(<([^>]+)>)/gi, "");
    },
    handleDeleteWaitingForPayment(e) {
      if (e.id === this.appointmentId) {
        this.$refs.Dialog.showAlertInfo(
          "มีการแก้ไขรายการรักษา",
          "กรุณาเริ่มทำรายการชำระเงินใหม่",
          "warning"
        ).then(() => {
          this.$bvModal.hide("PaymentModal");
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
$payment-rail-bg: #f5f6f8;
$payment-row-border: #e5e7eb;
$payment-accent-bar-w: 3px;

::v-deep .payment-modal-dialog {
  max-width: 1100px;
}

::v-deep .payment-modal-dialog .modal-body {
  padding: 0;
}

.payment-patient-strip {
  padding: 12px 20px;
  background: #ffffff;
  border-bottom: 2px solid $payment-row-border;
  display: flex;
  align-items: center;
  gap: 14px;
}

.payment-patient-avatar {
  flex-shrink: 0;
}

.payment-patient-info {
  flex: 1;
  min-width: 0;
  line-height: 1.35;
}

.payment-patient-name {
  font-size: 16px;
}

.payment-patient-meta {
  font-size: 12px;
  margin-top: 2px;
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.payment-patient-pills {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 4px;
}

.payment-patient-pill {
  font-weight: 500;
  font-size: 11px;
}

.payment-patient-actions {
  flex-shrink: 0;
}

.payment-body {
  display: grid;
  grid-template-columns: 1.55fr 1fr;
  min-height: 0;
}

.payment-left {
  padding: 18px 20px;
  overflow-y: auto;
  border-right: 1px solid $payment-row-border;
  display: flex;
  flex-direction: column;
  gap: 18px;
  max-height: 70vh;
}

.payment-right {
  background: $payment-rail-bg;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  position: sticky;
  top: 0;
  align-self: start;
  max-height: 70vh;
  overflow-y: auto;
}

/* ----- Section heading ----- */
.payment-section-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.payment-section-num {
  font-size: 12px;
  width: 22px;
  height: 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.payment-section-title {
  margin: 0;
  font-weight: 600;
  font-size: 15px;
}

.payment-section-nudge {
  font-weight: 500;
  font-size: 11px;
}

/* ----- Next visit ----- */
.payment-nextvisit-card {
  border: 1px solid #f0c674;
  background: #fffaea;
}

.payment-nextvisit-card--suggested {
  border-style: dashed;
  background: #fdfdfd;
}

.payment-nextvisit-grid {
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 14px;
  align-items: center;
}

.payment-nextvisit-date {
  text-align: center;
  background: #fff;
  border: 1px solid #d4ba3a;
  border-radius: 6px;
  padding: 6px 10px;
  min-width: 60px;
}

.payment-nextvisit-date--soft {
  border-color: $payment-row-border;
}

.payment-nextvisit-date-day {
  font-size: 22px;
  font-weight: 700;
  line-height: 1;
}

.payment-nextvisit-date-month {
  font-size: 11px;
  color: #555;
  text-transform: uppercase;
}

.payment-nextvisit-info-primary {
  font-weight: 600;
  font-size: 14px;
}

.payment-nextvisit-info-secondary {
  font-size: 12px;
}

.payment-nextvisit-suggested-header {
  font-size: 12px;
  font-weight: 500;
}

.payment-nextvisit-actions {
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: stretch;
}

.payment-nextvisit-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 14px;
  border: 1.5px dashed $payment-row-border;
  border-radius: 6px;
  background: transparent;
  color: #6c757d;
  font-size: 13px;
  cursor: pointer;
  transition: border-color 0.15s, color 0.15s;
}

.payment-nextvisit-empty:hover {
  border-color: #6c757d;
  color: #495057;
}

/* ----- Discount banner above items ----- */
.payment-discount-notice {
  background: #fff8e1;
  border: 1px solid #ffe58f;
  color: #8a6d3b;
  padding: 6px 10px;
  border-radius: 4px;
  font-size: 12px;
  margin-bottom: 8px;
}

/* ----- Empty items state ----- */
.payment-items-empty {
  text-align: center;
  padding: 30px 16px;
  border: 1px dashed $payment-row-border;
  border-radius: 6px;
  background: #fff;
}

/* ----- Item card ----- */
.payment-item-card {
  border: 1px solid $payment-row-border;
  border-left: $payment-accent-bar-w solid #cbd5e1;
  border-radius: 6px;
  background: #fff;
  padding: 10px 12px;
  margin-bottom: 8px;
}

.payment-item-card--lightblue {
  border-left-color: #93c5fd;
}
.payment-item-card--beige {
  border-left-color: #d4b896;
}
.payment-item-card--sky {
  border-left-color: #7dd3fc;
}
.payment-item-card--lightpurple {
  border-left-color: #c4b5fd;
}
.payment-item-card--skintone {
  border-left-color: #fdba74;
}
.payment-item-card--danger {
  border-left-color: #dc3545;
  background: #fff5f5;
}
.payment-item-card--dark {
  border-left-color: #6c757d;
  background: #f8f9fa;
}

.payment-item-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
}

.payment-item-info {
  flex: 1;
  min-width: 0;
}

.payment-item-title {
  font-weight: 600;
  font-size: 14px;
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 4px;
}

.payment-item-num {
  color: #6c757d;
  font-weight: 500;
  margin-right: 2px;
}

.payment-item-meta {
  font-size: 12px;
  margin-top: 2px;
}

.payment-item-paid {
  text-align: right;
  flex-shrink: 0;
  width: 110px;
}

.payment-item-paid-label {
  font-size: 11px;
  margin-bottom: 2px;
}

.payment-item-paid-input {
  font-weight: 600;
}

.payment-item-bottom {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px dashed $payment-row-border;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  font-size: 12px;
}

.payment-item-finals {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.payment-item-finals-sep {
  opacity: 0.5;
}

.payment-item-strike {
  text-decoration: line-through;
  margin-right: 4px;
  font-size: 11px;
}

.payment-item-actions {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 6px;
}

.payment-item-toggle {
  padding: 2px 8px;
}

.payment-item-discount-editor {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid $payment-row-border;
}

/* ----- Deposit row ----- */
.payment-deposit-row {
  margin-top: 12px;
  padding: 12px 14px;
  border: 1.5px dashed $payment-row-border;
  border-radius: 6px;
  background: #fafbfc;
}

.payment-deposit-amount {
  margin-top: 8px;
}

.payment-deposit-input {
  max-width: 220px;
}

/* ----- Right rail summary ----- */
.payment-summary-total {
  background: #fff;
  border: 1px solid $payment-row-border;
  border-radius: 8px;
  padding: 14px 16px;
}

.payment-summary-label {
  font-size: 12px;
  margin-bottom: 4px;
}

.payment-summary-amount {
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin-bottom: 4px;
}

.payment-summary-amount-value {
  font-size: 32px;
  font-weight: 700;
  line-height: 1;
}

.payment-summary-amount-unit {
  font-size: 14px;
}

.payment-summary-sub {
  font-size: 12px;
}

.payment-summary-card {
  background: #fff;
  border: 1px solid $payment-row-border;
  border-radius: 6px;
  padding: 10px 12px;
}

.payment-summary-card-label {
  font-size: 12px;
  margin-bottom: 4px;
  display: block;
}

.payment-right-spacer {
  flex: 1;
}

.payment-actions {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.payment-cta {
  font-weight: 600;
}

.payment-back i {
  margin-right: 0;
}

/* ----- Responsive: stack columns under 992px ----- */
@media (max-width: 991.98px) {
  .payment-body {
    grid-template-columns: 1fr;
  }

  .payment-left {
    border-right: none;
    border-bottom: 1px solid $payment-row-border;
    max-height: none;
    overflow-y: visible;
  }

  .payment-right {
    position: sticky;
    top: auto;
    bottom: 0;
    max-height: none;
    z-index: 1020;
    box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.06);
  }

  .payment-patient-strip {
    flex-wrap: wrap;
  }
}
</style>
